import React, { useState } from 'react';
import type { SubscriptionStatus } from '../../lib/types';

interface Props {
  subscriptionStatus: SubscriptionStatus;
  trialEndsAt: string | null;
  userRole: string;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-NZ', { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysRemaining(trialEndsAt: string): number {
  const ms = new Date(trialEndsAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 86400000));
}

export const TrialCountdownPill = React.memo(function TrialCountdownPill({
  subscriptionStatus, trialEndsAt, userRole,
}: Props) {
  const [modalOpen, setModalOpen] = useState(false);

  // Only show for trial subscriptions, not for super_admin, and only when data is available
  if (subscriptionStatus !== 'trial' || !trialEndsAt || userRole === 'super_admin') return null;

  const days = daysRemaining(trialEndsAt);
  // days === 0 means expired — App.tsx shows TrialExpiredScreen instead, so we won't reach here
  if (days === 0) return null;

  const isUrgent = days <= 1;
  const isWarning = days <= 5 && days > 1;

  const color = isUrgent ? 'var(--red)' : isWarning ? 'var(--amber)' : 'var(--text2)';
  const borderColor = isUrgent ? 'var(--red)' : isWarning ? 'var(--amber)' : 'var(--border)';
  const bg = isUrgent ? 'var(--red-bg)' : isWarning ? 'var(--amber-bg)' : 'var(--surface2)';

  const pillLabel = isUrgent ? 'Trial ends today' : `Trial: ${days} days left`;

  return (
    <>
      <button
        onClick={() => setModalOpen(true)}
        style={{
          display: 'inline-flex', alignItems: 'center',
          padding: '4px 10px',
          borderRadius: 20,
          border: `1px solid ${borderColor}`,
          background: bg,
          color,
          fontSize: 'var(--fs-xs)',
          fontWeight: 600,
          cursor: 'pointer',
          letterSpacing: .2,
          whiteSpace: 'nowrap',
          lineHeight: 1,
        }}
        title="Click for trial details"
      >
        {pillLabel}
      </button>

      {modalOpen && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 'var(--sp-4)',
          }}
          onClick={() => setModalOpen(false)}
        >
          <div
            className="card"
            style={{ maxWidth: 400, width: '100%', padding: 'var(--sp-6)' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="text-xl font-bold mb-1">Your trial</div>
            <div className="text-sm text-muted mb-4">
              Trial ends {formatDate(trialEndsAt)}
            </div>
            <div
              style={{
                padding: 'var(--sp-3)',
                borderRadius: 'var(--r)',
                background: isUrgent ? 'var(--red-bg)' : isWarning ? 'var(--amber-bg)' : 'var(--surface2)',
                color: isUrgent ? 'var(--red)' : isWarning ? 'var(--amber)' : 'var(--text2)',
                fontSize: 'var(--fs-sm)',
                fontWeight: 500,
                marginBottom: 'var(--sp-4)',
              }}
            >
              {days === 1 ? 'Your trial ends today.' : `${days} days remaining in your trial.`}
            </div>
            <p className="text-sm mb-5" style={{ color: 'var(--text2)', lineHeight: 1.5 }}>
              Contact us to activate your subscription before your trial ends. Your data is always preserved.
            </p>
            <div className="flex gap-2">
              <a
                className="btn btn-primary"
                href="mailto:hello@getgopal.co.nz"
                style={{ textDecoration: 'none', flex: 1, justifyContent: 'center' }}
              >
                Contact us
              </a>
              <button
                className="btn btn-ghost"
                onClick={() => setModalOpen(false)}
                style={{ flex: 1 }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});
